import { Button, FormControl, LinearProgress, Radio } from "@mui/material";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/system";
import * as React from "react";
import TextInput from "../TextInput/TextInput";
import classes from './MUIMultiSelect.module.css';

const CustomFormControl = styled(FormControl)({
  "& .MuiNativeSelect-select": {
    borderRadius: 4,
    padding: 9,
    fontSize: 14,
  },
});

const MUIMultiSelect = React.forwardRef(function MUIMultiSelect(
  {
    label,
    options,
    source,
    placeholder = "Select",
    value = [],
    onChange = () => { },
    onRadioChange = () => { }, // Callback for radio button changes
    loading,
    disabled,
    selectedPrimary = null,
    allowCustomOption = false,
    allowSettingPrimary = false,
    shouldShowValue = true,
    ...props
  },
  ref
) {
  //const [value, setValue] = React.useState(
  //  defaultValue.length > 0 ? defaultValue : [] // Default to the first menu item
  //);
  const [customOptions, setCustomOptions] = React.useState([]);
  const [newCustomOption, setNewCustomOption] = React.useState("");
  const [selectedRadio, setSelectedRadio] = React.useState(
    selectedPrimary // Default to the first menu item
  );
  const [selectedValues, setSelectedValues] = React.useState(
    value || []// Default to the first menu item
  );
  console.log("primary", selectedRadio, value);
  React.useImperativeHandle(ref, () => {
    return {
      getValue: () => value,
    };
  });

  React.useEffect(() => {
    if (value && value.length > 0) {
      setSelectedValues(value);
      setSelectedRadio(selectedPrimary); // Set the first value as the selected radio button   
    } else {
      setSelectedValues([]);
      setSelectedRadio(null); // Reset radio button if no value is selected
    }
  }, [value, selectedPrimary]);

  const handleChange = (event) => {
    const selectedValues = event.target.value;
    console.log("Selected values:", selectedValues);
    // If the deselected item was the one with the active radio button, reset the radio button
    if (!selectedValues.includes(selectedRadio)) {
      setSelectedRadio(null);
      onRadioChange(null);
    }
    if (selectedValues && selectedValues.length > 0 && !selectedValues.includes(selectedRadio)) {
      setSelectedRadio(selectedValues[0]);
    }
    setSelectedValues(selectedValues);
    onChange(selectedValues, selectedRadio || selectedValues?.[0]);
  };

  const handleAddCustomOption = () => {
    const trimmedOption = newCustomOption.trim();
    if (trimmedOption && !customOptions.includes(trimmedOption)) {
      setCustomOptions((prev) => [...prev, trimmedOption]);
      onChange([...value, trimmedOption]);
      setNewCustomOption(""); // Clear the input field
    }
  };

  const handleRadioChange = (optionValue) => {
    setSelectedRadio(optionValue);
    onRadioChange(optionValue);

    // Ensure the corresponding menu item remains selected
    if (!value.includes(optionValue)) {
      onChange([...value, optionValue], optionValue);
    } else {
      onChange(value, optionValue);
    }
  };

  const renderPrimarySelection = (option) => {
    if (allowSettingPrimary) {
      return (
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", width: "100%" }}>
          <Radio
            size="small"
            checked={selectedRadio === option.value}
            onChange={() => handleRadioChange(option.value)}
            onClick={(e) => {
              e.stopPropagation();
            }}
          />
          {selectedRadio === option.value && (
            <Typography variant="caption" color="primary" style={{ marginLeft: 4 }}>
              (primary)
            </Typography>
          )}
        </div>
      );
    }
  }

  const [modalOpen, setModalOpen] = React.useState(false);

  // Modal component for input
  const InputModal = () => {
    const inputRef = React.useRef(null);

    React.useEffect(() => {
      if (modalOpen && inputRef.current) {
        inputRef.current.focus();
      }
    }, [modalOpen]);

    return (
      <Box className={classes.modalBackdrop}>
        <Box className={classes.modalContent}>
          <Typography variant="h6" gutterBottom>
            Add New {label}
          </Typography>
          <TextInput
            disabled={false}
            fullWidth
            value={newCustomOption}
            label={`${label}`}
            placeholder={""}
            onChange={(e) => setNewCustomOption(e.target.value)}
            inputRef={inputRef}
          />
          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button
              onClick={() => setModalOpen(false)}
              variant="outlined"
              size="small"
              style={{ marginRight: 8 }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                handleAddCustomOption();
                setModalOpen(false);
              }}
              variant="contained"
              size="small"
              disabled={!newCustomOption.trim()}
            >
              Add
            </Button>
          </Box>
        </Box>
      </Box>
    );
  };

  // Implement onChange handler for Select component
  return (
    <Box className={classes.box}>
      <Box className={classes.boxSection}>
        <Typography className={classes.label}>{label}</Typography>
        <CustomFormControl fullWidth variant="outlined" size="small">
          {loading && <LinearProgress className={classes.progress} />}
          <Select
            multiple={true}
            value={selectedValues}
            onChange={handleChange}
            size="small"
            sx={{ minHeight: "40px" }}
            input={<OutlinedInput />}
            // renderValue={(selected) =>
            //   (selected.length > 0 && shouldShowValue) ? selected.join(", ") : placeholder
            // }
            renderValue={(selected) => {
              if (selected.length > 0 && shouldShowValue) {
                if (props.renderSelectedValues) {
                  return props.renderSelectedValues(selected);
                }
                return selected.join(", ");
              }
              return placeholder;
            }}
            {...props}
            filterOptions={(x) => x}
            MenuProps={{
              disableAutoFocus: true,
              autoFocus: false,
              disableAutoFocusItem: true,
            }}
          >
            {options.length > 0 &&
              options.map((option) => (
                <MenuItem dense={true} key={option.value} value={option.value}>
                  {/* MUI Checkbox */}
                  <input
                    type="checkbox"
                    checked={selectedValues.indexOf(option.value) > -1}
                    style={{ marginRight: 8 }}
                    readOnly
                  />
                  {option.name}
                  {renderPrimarySelection(option)}
                </MenuItem>
              ))}
            {customOptions.map((customOption) => (
              <MenuItem dense={true} key={customOption} value={customOption}>
                <input
                  type="checkbox"
                  checked={selectedValues.indexOf(customOption) > -1}
                  readOnly
                  style={{ marginRight: 8 }}
                />
                {customOption}
              </MenuItem>
            ))}
          </Select>
        </CustomFormControl>
      </Box>

      {allowCustomOption && (
        <Box>
          <Button
            onClick={() => setModalOpen(true)}
            variant="contained"
            size="small"
            className={classes.addButton}
            disabled={disabled}
          >
            +
          </Button>
        </Box>
      )}

      {allowCustomOption && modalOpen && <InputModal />}
    </Box>
  );
});

export default MUIMultiSelect;
